import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
} from "@/components/ui";
import {
  Download,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import reportsService from "@/services/reportsService";
import { FilterPanel } from "../filters";

/**
 * Transaction List Report Component
 *
 * Displays filterable, sortable, paginated list of transactions.
 *
 * Requirements: 5.1-5.10, 16.1-16.8
 */
const TransactionList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    subsidiary_id: "",
    date_from: "",
    date_to: "",
    account_ids: [],
    budget_ids: [],
    transaction_types: [],
    posting_statuses: [],
  });
  const [sorting, setSorting] = useState({
    column: "date",
    direction: "desc",
  });

  useEffect(() => {
    loadTransactions();
  }, [pagination.page, sorting]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      // The backend expects comma-separated strings for multi-select
      // filters, not repeated array query params, and page_size/sort_order
      // as the param names (not limit/sort_direction).
      const params = {
        ...filters,
        subsidiary_id: filters.subsidiary_id || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        account_ids: filters.account_ids?.length
          ? filters.account_ids.join(",")
          : undefined,
        budget_ids: filters.budget_ids?.length
          ? filters.budget_ids.join(",")
          : undefined,
        transaction_types: filters.transaction_types?.length
          ? filters.transaction_types.join(",")
          : undefined,
        posting_statuses: filters.posting_statuses?.length
          ? filters.posting_statuses.join(",")
          : undefined,
        page: pagination.page,
        page_size: pagination.limit,
        sort_by: sorting.column,
        sort_order: sorting.direction,
      };

      const response = await reportsService.getTransactionList(params);

      if (response?.data?.success) {
        setTransactions(response.data.data.transactions);
        setPagination({
          ...pagination,
          total: response.data.data.pagination.total_records,
          totalPages: response.data.data.pagination.total_pages,
        });
      }
    } catch (error) {
      console.error("Failed to load transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setPagination({ ...pagination, page: 1 });
    loadTransactions();
  };

  const handleClearFilters = () => {
    setFilters({
      subsidiary_id: "",
      date_from: "",
      date_to: "",
      account_ids: [],
      budget_ids: [],
      transaction_types: [],
      posting_statuses: [],
    });
    setPagination({ ...pagination, page: 1 });
  };

  const handleSort = (column) => {
    setSorting({
      column,
      direction:
        sorting.column === column && sorting.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const exportData = {
        reportData: { transactions },
        reportType: "transaction_list",
        reportTitle: "Transaction List",
        format,
        filters,
        auditTrail: "summary",
      };

      const response =
        format === "pdf"
          ? await reportsService.exportToPDF(exportData)
          : await reportsService.exportToExcel(exportData);

      if (response?.data?.success) {
        const downloadResponse = await reportsService.downloadExport(
          response.data.data.history_id,
        );
        const url = window.URL.createObjectURL(
          new Blob([downloadResponse.data]),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", response.data.data.filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₦${parseFloat(amount || 0).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-NG");
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: "secondary",
      posted: "success",
      cancelled: "destructive",
    };
    return <Badge variant={variants[status] || "secondary"}>{status}</Badge>;
  };

  const SortIcon = ({ column }) => {
    if (sorting.column !== column) return null;
    return sorting.direction === "asc" ? (
      <ArrowUp className="h-4 w-4 inline ml-1" />
    ) : (
      <ArrowDown className="h-4 w-4 inline ml-1" />
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Transaction List</h1>
          <p className="text-gray-600 mt-1">
            View and filter all financial transactions
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleExport("pdf")}
            disabled={exporting || transactions.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport("excel")}
            disabled={exporting || transactions.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <FilterPanel
        filters={filters}
        onFiltersChange={setFilters}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Transactions</CardTitle>
            <div className="text-sm text-gray-600">
              Showing {(pagination.page - 1) * pagination.limit + 1} -{" "}
              {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
              of {pagination.total}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No transactions found. Try adjusting your filters.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead
                        className="cursor-pointer"
                        onClick={() => handleSort("date")}
                      >
                        Date <SortIcon column="date" />
                      </TableHead>
                      <TableHead
                        className="cursor-pointer"
                        onClick={() => handleSort("transaction_number")}
                      >
                        Transaction # <SortIcon column="transaction_number" />
                      </TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Subsidiary</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead
                        className="text-right cursor-pointer"
                        onClick={() => handleSort("amount")}
                      >
                        Amount <SortIcon column="amount" />
                      </TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow
                        key={transaction.id}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() =>
                          navigate(`/finance/transactions/${transaction.id}`)
                        }
                      >
                        <TableCell>{formatDate(transaction.date)}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {transaction.transaction_number}
                        </TableCell>
                        <TableCell>{transaction.description}</TableCell>
                        <TableCell>
                          {transaction.subsidiary || "-"}
                        </TableCell>
                        <TableCell>
                          {transaction.debit_account
                            ? `${transaction.debit_account.code} - ${transaction.debit_account.name}`
                            : "-"}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(transaction.amount)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{transaction.type}</Badge>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(transaction.status)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.totalPages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPagination({
                        ...pagination,
                        page: pagination.page - 1,
                      })
                    }
                    disabled={pagination.page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPagination({
                        ...pagination,
                        page: pagination.page + 1,
                      })
                    }
                    disabled={pagination.page === pagination.totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TransactionList;
